import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_ROUTING_FORMS,
  GET_ROUTING_FORM,
  GET_EVENT_TYPES,
  SAVE_ROUTING_FORM,
  UPDATE_ROUTING_FORM,
  DELETE_ROUTING_FORM,
  ARCHIVE_ROUTING_FORM,
} from "../graphql/operations";

// Create context
const RoutingFormsContext = createContext(null);

// Define the provider component
export const RoutingFormsProvider = ({ children, spaceId }) => {
  const [forms, setForms] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apollo queries - pass spaceId to filter by space
  const {
    data: formsData,
    loading: formsLoading,
    error: formsError,
    refetch: refetchForms,
  } = useQuery(GET_ROUTING_FORMS, {
    variables: { spaceId }, // Filter by spaceId for multi-tenant isolation
    fetchPolicy: "network-only", // Don't cache to ensure fresh data
    skip: !spaceId, // Skip query if no spaceId
  });

  const {
    data: eventTypesData,
    loading: eventTypesLoading,
    error: eventTypesError,
  } = useQuery(GET_EVENT_TYPES);

  // Apollo mutations
  const [saveFormMutation] = useMutation(SAVE_ROUTING_FORM);
  const [updateFormMutation] = useMutation(UPDATE_ROUTING_FORM);
  const [deleteFormMutation] = useMutation(DELETE_ROUTING_FORM);
  const [archiveFormMutation] = useMutation(ARCHIVE_ROUTING_FORM);

  // Load forms and event types
  useEffect(() => {
    if (!formsLoading && !formsError && formsData) {
      // Use the routing forms specific query result from routingforms collection
      console.log(
        "📝 ROUTING FORMS from routingforms collection:",
        formsData.getAllRoutingForms?.length || 0,
        "routing forms"
      );
      setForms(formsData.getAllRoutingForms || []);
    }

    if (formsError) {
      console.error("Error loading routing forms:", formsError);
      setError(formsError);
    }

    if (!eventTypesLoading && !eventTypesError && eventTypesData) {
      setEventTypes(eventTypesData.getEventTypes);
    }

    setLoading(formsLoading || eventTypesLoading);
  }, [
    formsData,
    formsLoading,
    formsError,
    eventTypesData,
    eventTypesLoading,
    eventTypesError,
  ]);

  // Create a new routing form
  const createForm = useCallback(
    async (formData) => {
      try {
        console.log("Context: Creating form with data:", formData);

        // Only include fields that RoutingFormInput type accepts
        const mutationInput = {
          name: formData.name,
          description: formData.description || "",
          status: "active",
          spaceId: spaceId, // Include spaceId for multi-tenant isolation
          // Routing form specific fields
          fields: formData.fields || [],
          routes: formData.routes || [],
          fallback: formData.fallback || {
            type: "CUSTOM_PAGE",
            value: "Thank you for your submission!",
          },
          emailSettings: formData.emailSettings || {
            recipients: [],
            addAllMembers: false,
          },
        };

        console.log("Context: Mutation input:", mutationInput);

        const { data } = await saveFormMutation({
          variables: {
            input: mutationInput,
          },
        });

        console.log("Context: Mutation response:", data);

        // Update local state
        if (data?.createRoutingForm) {
          setForms((prev) => [...prev, data.createRoutingForm]);
          return { success: true, form: data.createRoutingForm };
        } else {
          console.error("No createRoutingForm in response data:", data);
          return { success: false, error: "No form data returned" };
        }
      } catch (err) {
        console.error("Error creating routing form:", err);
        return { success: false, error: err.message };
      }
    },
    [saveFormMutation, spaceId]
  );

  // Update an existing routing form
  const updateForm = useCallback(
    async (id, formData) => {
      try {
        // Check if only routing config is being updated
        if (formData.routes || formData.fallback) {
          // Use specific mutation for routing config updates
          const { data } = await updateFormMutation({
            variables: {
              id,
              input: {
                // Include only the fields that FormInput accepts
                data: formData.data || {},
                name: formData.name,
                description: formData.description,
                status: formData.status,
              },
            },
          });

          // Update local state
          setForms((prev) =>
            prev.map((form) => (form._id === id ? data.updateForm : form))
          );

          return { success: true, form: data.updateForm };
        } else {
          // Regular form update
          const { data } = await updateFormMutation({
            variables: {
              id,
              input: formData,
            },
          });

          // Update local state
          setForms((prev) =>
            prev.map((form) => (form._id === id ? data.updateForm : form))
          );

          return { success: true, form: data.updateForm };
        }
      } catch (err) {
        console.error("Error updating routing form:", err);
        return { success: false, error: err.message };
      }
    },
    [updateFormMutation]
  );

  // Delete a routing form
  const deleteForm = useCallback(
    async (id) => {
      try {
        const { data } = await deleteFormMutation({
          variables: { id },
        });

        // The mutation returns deleteRoutingForm, not deleteForm
        if (data.deleteRoutingForm) {
          // Update local state
          setForms((prev) => prev.filter((form) => form._id !== id));
        }

        return { success: data.deleteRoutingForm };
      } catch (err) {
        console.error("Error deleting routing form:", err);
        return { success: false, error: err.message };
      }
    },
    [deleteFormMutation]
  );

  // Archive a routing form
  const archiveForm = useCallback(
    async (id) => {
      try {
        const { data } = await archiveFormMutation({
          variables: { id },
        });

        // The mutation returns archiveRoutingForm, not archiveForm
        // Update local state
        setForms((prev) =>
          prev.map((form) => (form._id === id ? data.archiveRoutingForm : form))
        );

        return { success: true, form: data.archiveRoutingForm };
      } catch (err) {
        console.error("Error archiving routing form:", err);
        return { success: false, error: err.message };
      }
    },
    [archiveFormMutation]
  );

  // Toggle form active/inactive
  const toggleFormStatus = useCallback(
    async (id, isActive) => {
      try {
        const status = isActive ? "active" : "archived";

        // Find the current form to get its name (required field)
        const currentForm = forms.find((f) => f._id === id);
        if (!currentForm) {
          return { success: false, error: "Form not found" };
        }

        const { data } = await updateFormMutation({
          variables: {
            id,
            input: {
              name: currentForm.name, // name is required
              status,
            },
          },
        });

        // The mutation returns updateRoutingForm, not updateForm
        // Update local state
        setForms((prev) =>
          prev.map((form) => (form._id === id ? data.updateRoutingForm : form))
        );

        return { success: true, form: data.updateRoutingForm };
      } catch (err) {
        console.error("Error toggling form status:", err);
        return { success: false, error: err.message };
      }
    },
    [updateFormMutation, forms]
  );

  // Get a single form by ID
  const getFormById = useCallback(
    (id) => {
      return forms.find((form) => form._id === id) || null;
    },
    [forms]
  );

  // Context value
  const value = {
    forms,
    eventTypes,
    loading,
    error,
    createForm,
    updateForm,
    deleteForm,
    archiveForm,
    toggleFormStatus,
    getFormById,
    refetchForms,
  };

  return (
    <RoutingFormsContext.Provider value={value}>
      {children}
    </RoutingFormsContext.Provider>
  );
};

// Custom hook to use the context
export const useRoutingForms = () => {
  const context = useContext(RoutingFormsContext);
  if (!context) {
    throw new Error(
      "useRoutingForms must be used within a RoutingFormsProvider"
    );
  }
  return context;
};
